import React, { useState, useEffect } from "react";
import { useLoaderData, useNavigate, useParams, useLocation } from "react-router-dom";
import MeaPage from "../../components/MeaPage";
import { useAuthContext } from "../../hooks/useAuthContext";
import classesAPI from "../../services/classes";
import trainingsAPI from "../../services/trainings";
import accountsAPI from "../../services/accounts";
import { Dropdown } from "primereact/dropdown";
import { Skeleton } from "primereact/skeleton";
import MeaLink from "../../components/MeaLink";
import { MeterGroup } from 'primereact/metergroup';

export function TeacherClassesPage() {
  const { school } = useLoaderData();
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, currentMembership, updateCurrentMembershipPreferences } = useAuthContext();
  const [pendingTrainingId, setPendingTrainingId] = useState(null);

  const isAdmin = location.pathname.includes("/admin");
  const basePath = isAdmin ? "admin" : "teacher";
  const spaceName = isAdmin ? "Administration" : "Espace Enseignant";

  const activeTrainingId = pendingTrainingId || trainingId;

  const trainingQuery = trainingsAPI.GetOne(school?.id, activeTrainingId, auth?.access_token);
  const training = trainingQuery?.data?.data;

  const trainingsQuery = trainingsAPI.useGetList(
    school?.id,
    auth?.access_token,
    1,
    100,
    ""
  );
  const trainings = trainingsQuery?.data?.data || [];

  const classesQuery = classesAPI.useGetList(
    school?.id,
    activeTrainingId,
    auth?.access_token,
    1,
    1000,
    isAdmin ? {} : { teacher_member_id: currentMembership?.id }
  );

  const classes = classesQuery?.data?.data || [];

  const totalStudentsSum = classes.reduce((sum, c) => sum + (c.total_students || 0), 0);
  const totalPassedSum = classes.reduce((sum, c) => sum + (c.total_passed || 0), 0);
  const totalFailedSum = classes.reduce((sum, c) => sum + (c.total_failed || 0), 0);
  const totalOngoingSum = classes.reduce((sum, c) => sum + (c.total_ongoing || 0), 0);

  const globalPassedPercent = totalStudentsSum > 0 ? Math.round((totalPassedSum / totalStudentsSum) * 100) : 0;
  const globalFailedPercent = totalStudentsSum > 0 ? Math.round((totalFailedSum / totalStudentsSum) * 100) : 0;
  const globalOngoingPercent = totalStudentsSum > 0 ? Math.round((totalOngoingSum / totalStudentsSum) * 100) : 0;

  const globalMeterValues = [
    { label: 'Passage (Admis)', value: globalPassedPercent, color: '#22c55e', bgColor: '#f0fdf4', icon: 'pi pi-check-circle', originalValue: totalPassedSum },
    { label: 'Redoublement', value: globalFailedPercent, color: '#ef4444', bgColor: '#fef2f2', icon: 'pi pi-exclamation-triangle', originalValue: totalFailedSum },
    { label: 'Non encore décidés', value: globalOngoingPercent, color: '#9ca3af', bgColor: '#f3f4f6', icon: 'pi pi-clock', originalValue: totalOngoingSum }
  ];

  const globalLabelList = ({ values }) => (
    <div className="grid w-full mt-2">
      {values.map((item, index) => (
        <div className="col-12 md:col-4 p-2" key={index}>
          <div className="surface-card p-3 border-round shadow-1 border-1 border-100 flex flex-column gap-2">
            <span className="text-500 text-sm">{item.label}</span>
            <div className="flex justify-content-between align-items-center">
              <div className="flex align-items-baseline gap-2">
                <span className="font-semibold text-2xl">{item.value}%</span>
                <span className="text-500 text-xs">({item.originalValue} {item.originalValue > 1 ? 'élèves' : 'élève'})</span>
              </div>
              <div className="flex align-items-center justify-content-center border-circle" style={{ width: '40px', height: '40px', backgroundColor: item.bgColor }}>
                <i className={`${item.icon} text-xl`} style={{ color: item.color }}></i>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const getStatusLabel = (status) => {
    switch (status) {
      case 'OPEN': return 'Ouvert';
      case 'DRAFT': return 'Brouillon';
      case 'SUSPENDED': return 'Suspendu';
      case 'ARCHIVED': return 'Archivé';
      default: return status;
    }
  };

  const itemTemplate = (option) => {
    return (
      <div className="flex align-items-center">
        <span className="font-medium text-color">{option.name}</span>
        <span className={`text-xs px-2 py-1 border-round font-semibold ml-2 ${
          option.status === 'OPEN' ? 'bg-green-100 text-green-800' :
          option.status === 'DRAFT' ? 'bg-blue-100 text-blue-800' :
          option.status === 'SUSPENDED' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {getStatusLabel(option.status)}
        </span>
      </div>
    );
  };

  const valueTemplate = (option, props) => {
    if (!option) {
      const currentReg = trainings.find(t => t.id === activeTrainingId) || training;
      if (!currentReg) {
        return <span>{props.placeholder}</span>;
      }
      option = currentReg;
    }
    return (
      <div className="flex align-items-center">
        <span className="font-bold text-color mr-2">{option.name}</span>
        <span className={`text-xs px-2 py-0.5 border-round font-semibold ${
          option.status === 'OPEN' ? 'bg-green-100 text-green-800' :
          option.status === 'DRAFT' ? 'bg-blue-100 text-blue-800' :
          option.status === 'SUSPENDED' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {getStatusLabel(option.status)}
        </span>
      </div>
    );
  };

  const handleTrainingChange = async (e) => {
    const newTrainingId = e.value;
    if (newTrainingId && newTrainingId !== trainingId) {
      setPendingTrainingId(newTrainingId);
      try {
        const updatedPrefs = await accountsAPI.UpdateMemberPreferences(auth?.access_token, currentMembership?.id, newTrainingId);
        if (updatedPrefs) {
          updateCurrentMembershipPreferences(updatedPrefs);
        } else {
          const fallbackPrefs = currentMembership?.preferences 
            ? { ...currentMembership.preferences, last_visited_training: newTrainingId }
            : { last_visited_training: newTrainingId };
          updateCurrentMembershipPreferences(fallbackPrefs);
        }
      } catch (err) {
        console.error("Failed to update last visited preference", err);
      }
      navigate(`/school/${school?.id}/${basePath}/avis-passage/${newTrainingId}`);
    }
  };

  useEffect(() => {
    if (pendingTrainingId && pendingTrainingId === trainingId) {
      setPendingTrainingId(null);
    }
  }, [pendingTrainingId, trainingId]);

  const sectionOptions = isAdmin ? [
    { label: "Inscriptions", value: "candidates" },
    { label: "Paiements", value: "payments" },
    { label: "Campagnes d'e-mails", value: "campaigns" },
    { label: "Gestion de l'espace famille", value: "settings" },
    { label: "Avis de passage", value: "avis-passage" },
    { label: "Suivi des observations", value: "student-observations" },
  ] : [
    { label: "Mes séances de cours", value: "sessions" },
    { label: "Mes classes", value: "classes" },
    { label: "Avis de passage", value: "avis-passage" },
    { label: "Agenda", value: "agenda" },
  ];

  const handleSectionChange = (e) => {
    const newSection = e.value;
    if (!newSection || newSection === "avis-passage") {
      return;
    }

    if (isAdmin) {
      if (newSection === "student-observations") {
        navigate(`/school/${school?.id}/admin/student-observations/${trainingId}`);
        return;
      }
      navigate(`/school/${school?.id}/admin/registrations/${trainingId}/${newSection}`);
      return;
    }

    if (newSection === "sessions") {
      navigate(`/school/${school?.id}/teacher/sessions/${activeTrainingId}`);
      return;
    }

    if (newSection === "classes") {
      navigate(`/school/${school?.id}/teacher/classes/${activeTrainingId}`);
      return;
    }

    if (newSection === "agenda") {
      navigate(`/school/${school?.id}/teacher/agenda/${activeTrainingId}`);
    }
  };

  const customWorkspaceBar = (
    <div className="w-full p-breadcrumb border-bottom-1 surface-border py-2 px-3 flex align-items-center flex-wrap gap-2">
      <div className="flex align-items-center gap-2">
        <i className="pi pi-th-large text-teal-600 text-sm"></i>
        <Dropdown
          value="avis-passage"
          options={sectionOptions}
          onChange={handleSectionChange}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: 'auto' }}
        />
      </div>
      <span className="text-300 font-light text-base sm:text-lg">/</span>
      <div className="flex align-items-center gap-2">
        <i className="pi pi-bookmark text-indigo-500 text-sm"></i>
        <Dropdown
          value={activeTrainingId}
          options={trainings}
          optionLabel="name"
          optionValue="id"
          onChange={handleTrainingChange}
          placeholder={training?.name || "Sélectionner une formation"}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: 'auto' }}
          itemTemplate={itemTemplate}
          valueTemplate={valueTemplate}
        />
      </div>
    </div>
  );

  return (
    <MeaPage
      showBackButton={!isAdmin}
      onBackButtonClick={() => navigate(`/school/${school?.id}/${basePath}`)}
      title="Avis de passage"
      breadcrumb={customWorkspaceBar}
    >
      <div className="grid grid-nogutter pt-1 px-4 md:px-6 lg:px-8">
        <div className="col-12 px-3 pt-1 pb-2">
          <p className="text-600 text-sm mb-4">
            {isAdmin
              ? "Veuillez sélectionner une classe ci-dessous pour renseigner ou modifier les avis de passage et les appréciations des élèves."
              : "Veuillez sélectionner l'une de vos classes ci-dessous pour renseigner les avis de passage et les appréciations des élèves."
            }
          </p>
        </div>

        {isAdmin && classesQuery.isSuccess && classes.length > 0 && totalStudentsSum > 0 && (
          <div className="col-12 px-3 py-1 mb-2">
            <h3 className="text-lg font-bold text-900 mb-2 mt-0 flex align-items-center gap-2">
              <i className="pi pi-chart-line text-teal-600"></i> Statut global des avis (Toutes les classes)
            </h3>
            <MeterGroup 
              values={globalMeterValues} 
              labelList={globalLabelList}
              className="w-full"
            />
          </div>
        )}

        {classesQuery.isLoading && (
          <div className="col-12 p-3">
            <Skeleton width="150px" height="24px" className="mb-3" />
            <div className="grid">
              {[1, 2].map((i) => (
                <div key={i} className="col-12 md:col-6 xl:col-4 p-3">
                  <Skeleton height="150px" />
                </div>
              ))}
            </div>
          </div>
        )}

        {classesQuery.isSuccess && classes.length === 0 && (
          <div className="col-12 p-3 text-center my-5 w-full">
            <i className="pi pi-folder-open text-400 text-5rem mb-3"></i>
            <div className="text-900 text-xl font-bold mb-1">
              {isAdmin ? "Aucune classe trouvée" : "Aucune classe assignée"}
            </div>
            <div className="text-600 text-sm">
              {isAdmin
                ? "Il n'y a pas de classe enregistrée dans cette formation."
                : "Vous n'êtes assigné(e) à aucune classe dans la formation sélectionnée."
              }
            </div>
          </div>
        )}

        {classesQuery.isSuccess && classes.length > 0 && (
          <div className="grid w-full">
            {classes.map((classe) => {
              const total = classe.total_students || 0;
              const passed = classe.total_passed || 0;
              const failed = classe.total_failed || 0;
              const ongoing = classe.total_ongoing || 0;

              const passedPercent = total > 0 ? Math.round((passed / total) * 100) : 0;
              const failedPercent = total > 0 ? Math.round((failed / total) * 100) : 0;
              const ongoingPercent = total > 0 ? Math.round((ongoing / total) * 100) : 0;

              return (
                <div key={classe.id} className="col-12 md:col-6 xl:col-4 p-3">
                  <div
                    className="surface-card border-round p-4 cursor-pointer shadow-1 hover:shadow-4 hover:border-teal-500 transition-all transition-duration-200 flex flex-column h-full"
                    style={{
                      borderLeft: "5px solid var(--teal-500)",
                      borderRadius: "8px",
                    }}
                    onClick={() =>
                      navigate(
                        isAdmin
                          ? `/school/${school?.id}/admin/trainings/${trainingId}/classes/${classe.id}/avis-passage`
                          : `/school/${school?.id}/teacher/trainings/${trainingId}/classes/${classe.id}`
                      )
                    }
                  >
                    <div className="flex align-items-center mb-3">
                      <div
                        className="mr-3 flex align-items-center justify-content-center bg-teal-50 text-teal-600 border-round"
                        style={{ width: "48px", height: "48px" }}
                      >
                        <i className="pi pi-book text-xl"></i>
                      </div>
                      <div className="flex flex-column align-items-start">
                        <span className="text-xl text-900 font-bold mb-1">{classe.name}</span>
                        {classe.level && (
                          <span className="bg-teal-50 text-teal-700 px-2 py-0.5 border-round text-xs font-semibold">
                            {classe.level.name}
                          </span>
                        )}
                      </div>
                    </div>

                    {total > 0 && (
                      <div className="mt-2 mb-4">
                        <MeterGroup 
                          values={[
                            { label: 'Passage', value: passedPercent, color: 'var(--green-500)' },
                            { label: 'Redoubl.', value: failedPercent, color: 'var(--red-500)' },
                            { label: 'Non déc.', value: ongoingPercent, color: 'var(--gray-400)' }
                          ]} 
                          labelList={(props) => (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {props.values.map((item, index) => (
                                <div key={index} className="flex align-items-center gap-1">
                                  <div className="border-circle" style={{ backgroundColor: item.color, width: '6px', height: '6px' }}></div>
                                  <span className="text-600" style={{ fontSize: '10px' }}>{item.label} ({item.value}%)</span>
                                </div>
                              ))}
                            </div>
                          )}
                        />
                      </div>
                    )}

                    <div className="flex align-items-center justify-content-between mt-auto pt-3 border-top-1 border-200">
                      <div className="flex align-items-center flex-wrap gap-2">
                        <span className="text-600 text-sm flex align-items-center gap-2">
                          <i className="pi pi-users text-teal-500"></i>
                          {total} {total > 1 ? "élèves" : "élève"}
                        </span>
                        {classe.referent && (
                          <>
                            <span className="text-400 text-sm">|</span>
                            <span className="text-600 text-sm flex align-items-center gap-1">
                              <i className="pi pi-user text-teal-500"></i>
                              {classe.referent.name
                                ? classe.referent.name
                                : `${classe.referent.lastname?.toUpperCase() || ""} ${classe.referent.firstname || ""}`.trim()}
                            </span>
                          </>
                        )}
                      </div>
                      <span className="text-teal-600 font-semibold text-xs flex align-items-center gap-1 hover:text-teal-800">
                        Accéder <i className="pi pi-arrow-right text-xs"></i>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MeaPage>
  );
}
